import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { HelperFunctions } from '@shared/utils/HelperFunctions';
import { PersonaDashboard } from '@app/core/auth/services/auth-session.service';
import { AuthSessionService } from '@app/core/auth/services/auth-session.service';
import { APP_CONSTANTS } from '@app/core/constants/app-constants';
import { HttpClientService } from '@app/core/interceptors/http-interceptors';
import { LoggerService } from '@app/core/services/logger.service';

type UnknownRecord = Record<string, unknown>;

interface UserDetailsApiResponse {
  readonly result?: unknown;
  readonly [key: string]: unknown;
}

interface PersonaApiResponse {
  readonly result?: unknown;
  readonly [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class UserDetailsService {
  private readonly http = inject(HttpClientService);
  private readonly logger = inject(LoggerService);
  private readonly router = inject(Router);
  private readonly authSession = inject(AuthSessionService);

  private readonly userSysIdState = signal('');
  private readonly userTypeState = signal('');
  private readonly userDetailsState = signal<UnknownRecord | null>(null);
  private readonly roleDetailsState = signal<readonly UnknownRecord[]>([]);
  private readonly loadedEmailState = signal('');
  private readonly isLoadingState = signal(false);

  readonly userSysId = this.userSysIdState.asReadonly();
  readonly userType = this.userTypeState.asReadonly();
  readonly userDetails = this.userDetailsState.asReadonly();
  readonly roleDetails = this.roleDetailsState.asReadonly();
  readonly loadedEmail = this.loadedEmailState.asReadonly();
  readonly isLoading = this.isLoadingState.asReadonly();

  async loadUserContextByEmail(email: string): Promise<void> {
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      this.clear();
      return;
    }

    if (this.loadedEmailState() === normalizedEmail && this.userDetailsState() !== null) {
      return;
    }

    this.isLoadingState.set(true);
    try {
      const endpoint = `${APP_CONSTANTS.general.USER_DETAILS_ENDPOINT}/${HelperFunctions.encodeUrlSegment(normalizedEmail)}`;
      const response = await firstValueFrom(this.http.get<UserDetailsApiResponse>(endpoint));
      const payload = this.unwrapPayload(response);
      const userDetails = this.extractUserDetails(payload);
      const roleDetails = this.extractRoleDetails(payload);
      const userSysId = this.extractSysId(userDetails, payload);
      const userType = this.extractUserType(userDetails, payload);

      this.userDetailsState.set(userDetails);
      this.roleDetailsState.set(roleDetails);
      this.userSysIdState.set(userSysId);
      this.userTypeState.set(userType);
      this.loadedEmailState.set(normalizedEmail);
      this.authSession.updateResolvedUserSysId(userSysId);
      await this.loadPersonaDetails(userSysId);
    } catch (error) {
      this.clear();

      if (this.isNoUserFoundForEmailError(error)) {
        this.authSession.clearImpersonatedEmail();
        this.logger.warn('No user found for provided email. Redirecting to login page.');
        void this.router.navigateByUrl('/login');
        return;
      }

      this.logger.error('Failed to load logged-in user context from user details API');
    } finally {
      this.isLoadingState.set(false);
    }
  }

  clear(): void {
    this.userSysIdState.set('');
    this.userTypeState.set('');
    this.userDetailsState.set(null);
    this.roleDetailsState.set([]);
    this.loadedEmailState.set('');
    this.authSession.clearPersonaDetails();
  }

  async loadPersonaDetails(userSysId: string): Promise<void> {
    const normalizedSysId = userSysId.trim();
    if (!normalizedSysId) {
      this.authSession.clearPersonaDetails();
      return;
    }

    try {
      const endpoint = APP_CONSTANTS.general.PERSONAS_ENDPOINT.replace(
        '{sys_id}',
        HelperFunctions.encodeUrlSegment(normalizedSysId),
      );
      const response = await firstValueFrom(this.http.get<PersonaApiResponse>(endpoint));
      const persona = this.extractPersonaPermissions(response?.result);
      this.authSession.setPersonaDetails(persona);
    } catch (error) {
      this.authSession.clearPersonaDetails();
      this.logger.error(
        'Failed to load persona details from persona API',
        this.extractErrorMessage(error),
      );
    }
  }

  private unwrapPayload(response: UserDetailsApiResponse): unknown {
    if (this.isRecord(response) && 'result' in response) {
      return response.result;
    }

    return response;
  }

  private extractUserDetails(payload: unknown): UnknownRecord | null {
    const root = this.resolveSingleRecord(payload);
    if (!root) {
      return null;
    }

    const userCandidates = [
      root['user_details'],
      root['userDetails'],
      root['user'],
      root['me_user'],
    ] as const;

    for (const candidate of userCandidates) {
      const userRecord = this.resolveSingleRecord(candidate);
      if (userRecord) {
        return userRecord;
      }
    }

    return root;
  }

  private extractRoleDetails(payload: unknown): readonly UnknownRecord[] {
    const root = this.resolveSingleRecord(payload);
    if (!root) {
      return [];
    }

    const roleCandidates = [
      root['role_details'],
      root['roleDetails'],
      root['roles'],
      root['role'],
    ] as const;

    for (const candidate of roleCandidates) {
      const resolvedRoles = this.resolveRoleCollection(candidate);
      if (resolvedRoles.length > 0) {
        return resolvedRoles;
      }
    }

    return [];
  }

  private resolveRoleCollection(value: unknown): readonly UnknownRecord[] {
    if (Array.isArray(value)) {
      return value.filter((item): item is UnknownRecord => this.isRecord(item));
    }

    if (this.isRecord(value)) {
      return [value];
    }

    return [];
  }

  private extractSysId(userDetails: UnknownRecord | null, payload: unknown): string {
    const sysIdKeys = ['sys_id', 'user_sys_id', 'u_user_sys_id', 'uid', 'sub'] as const;

    for (const key of sysIdKeys) {
      const userValue = userDetails?.[key];
      if (typeof userValue === 'string' && userValue.trim()) {
        return userValue.trim();
      }
    }

    const root = this.resolveSingleRecord(payload);
    if (!root) {
      return '';
    }

    for (const key of sysIdKeys) {
      const rootValue = root[key];
      if (typeof rootValue === 'string' && rootValue.trim()) {
        return rootValue.trim();
      }
    }

    return '';
  }

  private extractUserType(userDetails: UnknownRecord | null, payload: unknown): string {
    const userType = userDetails?.['user_type'];
    if (typeof userType === 'string') {
      return userType.trim();
    }

    const root = this.resolveSingleRecord(payload);
    const rootUserType = root?.['user_type'];
    return typeof rootUserType === 'string' ? rootUserType.trim() : '';
  }

  private resolveSingleRecord(value: unknown): UnknownRecord | null {
    if (this.isRecord(value)) {
      return value;
    }

    if (Array.isArray(value)) {
      const firstRecord = value.find((item): item is UnknownRecord => this.isRecord(item));
      return firstRecord ?? null;
    }

    return null;
  }

  private isRecord(value: unknown): value is UnknownRecord {
    return typeof value === 'object' && value !== null;
  }

  private extractPersonaPermissions(payload: unknown): {
    personaSysId: string;
    name: string;
    dashboards: readonly PersonaDashboard[];
    actionInsights: boolean;
    priorityAction: boolean;
    aiCockpit: boolean;
    invoice: boolean;
    raid: boolean;
    chatbot: boolean;
    delegation: boolean;
    powerBi: boolean;
  } | null {
    if (!this.isRecord(payload)) {
      return null;
    }

    const personaNode = payload['persona'];
    if (!this.isRecord(personaNode)) {
      return null;
    }

    return {
      personaSysId: this.readStringValue(personaNode['persona_sys_id']),
      name: this.readStringValue(personaNode['name']),
      dashboards: this.extractPersonaDashboards(personaNode['dashboards']),
      actionInsights: this.readBooleanValue(personaNode['action_insights']),
      priorityAction: this.readBooleanValue(personaNode['priority_action']),
      aiCockpit: this.readBooleanValue(personaNode['ai_cockpit']),
      invoice: this.readBooleanValue(personaNode['invoice']),
      raid: this.readBooleanValue(personaNode['raid']),
      chatbot: this.readBooleanValue(personaNode['chatbot']),
      delegation: this.readBooleanValue(personaNode['delegation'] ?? personaNode['delegate']),
      powerBi: this.readBooleanValue(personaNode['power_bi']),
    };
  }

  private extractPersonaDashboards(value: unknown): readonly PersonaDashboard[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => {
        if (!this.isRecord(item)) {
          return null;
        }

        const dashboardSysId = this.readStringValue(item['dashboard_sys_id']);
        const dashboardName = this.readStringValue(item['name']);
        const workspace = this.readStringValue(item['workspace']);
        const chart = this.readStringValue(item['chart']);
        const section = this.readStringValue(item['section']);

        if (!dashboardSysId || !workspace || !chart || !section) {
          return null;
        }

        return {
          dashboardSysId,
          name: dashboardName || dashboardSysId,
          workspace,
          chart,
          section,
          env: this.readStringValue(item['env']),
          key: this.readStringValue(item['key']),
        } as PersonaDashboard;
      })
      .filter((item): item is PersonaDashboard => item !== null);
  }

  private readStringValue(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private readBooleanValue(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      return value === 1;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }

    return false;
  }

  private isNoUserFoundForEmailError(error: unknown): boolean {
    const message = this.extractErrorMessage(error).toLowerCase();
    return message.includes('no user found for email');
  }

  private extractErrorMessage(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error instanceof Error) {
      return error.message;
    }

    if (!this.isRecord(error)) {
      return '';
    }

    const directMessage = error['message'];
    if (typeof directMessage === 'string' && directMessage.trim()) {
      return directMessage.trim();
    }

    const nestedError = error['error'];
    if (typeof nestedError === 'string' && nestedError.trim()) {
      return nestedError.trim();
    }

    if (this.isRecord(nestedError)) {
      const nestedMessage = nestedError['message'];
      if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
        return nestedMessage.trim();
      }

      const nestedDetail = nestedError['detail'];
      if (typeof nestedDetail === 'string' && nestedDetail.trim()) {
        return nestedDetail.trim();
      }
    }

    const nestedResult = error['result'];
    if (typeof nestedResult === 'string' && nestedResult.trim()) {
      return nestedResult.trim();
    }

    return '';
  }
}
